import { call, put, select, takeLatest } from 'redux-saga/effects'
import { filter } from 'lodash'

import createDataDogLog from '../utils/datadog'

import {
  makeGetAttendantByPin,
  getEmployeeApprovalCodes,
  getEmployeeIds,
  makeGetAttendantByEmployeeId,
} from '../selectors/attendant'

import {
  setActiveAttendantId,
  clearAttemptPinLoginStatus,
  clearFetchAttendantsStatus,
  LOGOUT,
  REGISTER_LOGIN,
} from '../actions/attendant'

import {
  ATTEMPT_PIN_LOGIN,
  FETCH_ATTENDANTS,
  fetchAttendantsSucceeded,
  fetchAttendantsFailed,
  attemptPinLoginSucceeded,
  attemptPinLoginFailed,
} from '../actions/attendant'

import { fetchAttendantSettingsFailed } from '../actions/attendantSettings'

import StadiumRemote from '../remote/stadiumRemote'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { clearCart } from '../actions/cart'
import { clearOrderInProgress, setCurrentOrderId } from '../actions/order'
import { isNetworkError } from '../VNUtil/Errors'
import { setNetworkAvailable } from '../actions/config'
import { getNetworkAvailableValue } from '../utils/networkConnected'

const getAttendantByPin = makeGetAttendantByPin()
const getAttendantByEmployeeId = makeGetAttendantByEmployeeId()

const filterAttendants = (attendants = []) => filter(attendants, (attendant) => attendant?.deactivatedAt === null)

export function* fetchAttendantsSaga() {
  const organizationName = yield select((state) => state?.config?.organizationName ?? '')
  try {
    const result = yield call(StadiumRemote.getAttendants, organizationName)
    const activeAttendants = filterAttendants(result?.data?.attendants)
    yield put(fetchAttendantsSucceeded(activeAttendants))
  } catch (err) {
    if (isNetworkError(err)) {
      yield put(setNetworkAvailable(false))
    }
    yield put(fetchAttendantsFailed(err))
  }
}

export function* watchFetchAttendantsSaga() {
  yield takeLatest(FETCH_ATTENDANTS, fetchAttendantsSaga)
}

export function* watchAttemptPinLoginSaga() {
  yield takeLatest(ATTEMPT_PIN_LOGIN, attemptPinLoginSaga)
}

export function* watchRegisterLoginSaga() {
  yield takeLatest(REGISTER_LOGIN, registerLoginSaga)
}

function* registerLoginSaga(action) {
  const networkConnected = getNetworkAvailableValue()
  if (!networkConnected) return

  try {
    yield call(StadiumRemote.getAttendantSettings, action?.payload?.pin)
  } catch (error) {
    createDataDogLog('error', 'An error occurred while logging in.')
    if (isNetworkError(error)) {
      yield put(setNetworkAvailable(false))
    }
  }
}

function* attemptPinLoginSaga(action) {
  const pin = action?.payload?.pin

  const approvalCodes = yield select(getEmployeeApprovalCodes)
  // we are not checking for manager Id since that should have already been checked for.
  const employeeIds = yield select(getEmployeeIds)

  if (approvalCodes?.includes(pin)) {
    const attendant = yield select(getAttendantByPin, pin)
    const attendantUuid = attendant?.uuid

    yield put(setActiveAttendantId(attendantUuid))
    yield put(attemptPinLoginSucceeded())
  } else if (employeeIds?.includes(pin)) {
    const attendant = yield select(getAttendantByEmployeeId, pin)
    const attendantUuid = attendant?.uuid

    yield put(setActiveAttendantId(attendantUuid))
    yield put(attemptPinLoginSucceeded())
  } else {
    yield put(fetchAttendantSettingsFailed({ errorMessage: 'Attendant not found' }))
    yield put(attemptPinLoginFailed())
  }
}

function* logoutSaga() {
  yield put(setActiveAttendantId(undefined))
  yield put(clearAttemptPinLoginStatus())
  yield put(clearFetchAttendantsStatus())
  yield put(clearPromotionsOnOrder())
  yield put(clearCart())
  yield put(setCurrentOrderId(null))
  yield put(clearOrderInProgress())
}

export function* watchLogoutSaga() {
  yield takeLatest(LOGOUT, logoutSaga)
}
